/**
 * Secure Authentication Service
 * Handles all authentication through backend API (no Firebase config exposed)
 */

import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../firebase';
import { lockKeys } from './chat/chatKeys';
import { resetPasskeySessionState } from './chat/chatPasskeys';
import { deleteTrustedRecord } from './chat/chatTrustedBrowser';

export interface User {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
}

interface AuthResponse {
  success: boolean;
  token: string;
  user: User;
}

class SecureAuthService {
  private baseUrl: string;
  private token: string | null = null;
  private user: User | null = null;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002';
    this.loadStoredAuth();
  }

  /**
   * Load stored authentication from localStorage
   */
  private loadStoredAuth(): void {
    try {
      const storedToken = localStorage.getItem('auth_token');
      const storedUser = localStorage.getItem('auth_user');
      
      if (storedToken && storedUser) {
        this.token = storedToken;
        this.user = JSON.parse(storedUser);
      }
    } catch (error) {
      this.clearAuth();
    }
  }

  /**
   * Store authentication data
   */
  private storeAuth(token: string, user: User): void {
    if (this.user && this.user.uid !== user.uid) {
      lockKeys();
      resetPasskeySessionState();
    }
    this.token = token;
    this.user = user;
    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_user', JSON.stringify(user));
  }

  /**
   * Clear authentication data
   */
  private clearAuth(): void {
    lockKeys();
    resetPasskeySessionState();
    this.token = null;
    this.user = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
  }

  /**
   * Login with email and password
   */
  async login(email: string, password: string): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => {
          return {};
        });
        
        // Check for different error response formats
        // Backend returns: { error: "message", ... } or { message: "...", ... }
        const errorMessage = errorData.error || 
                            errorData.message || 
                            errorData.error?.message || 
                            errorData.details?.[0]?.message ||
                            (response.status === 401 ? 'Invalid email or password' : 'Login failed');
        
        throw new Error(errorMessage);
      }

      const data: AuthResponse = await response.json();
      
      if (data.success && data.token && data.user) {
        // Backend returns a custom token - exchange it for an ID token
        try {
          const userCredential = await signInWithCustomToken(auth, data.token);
          const idToken = await userCredential.user.getIdToken();
          
          // Store the ID token instead of custom token
          this.storeAuth(idToken, data.user);
          
          return {
            ...data,
            token: idToken  // Return ID token
          };
        } catch (firebaseError: any) {
          throw new Error('Failed to complete authentication. Please try again.');
        }
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Register new user
   */
  async register(email: string, password: string, displayName: string): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, displayName }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Registration failed');
      }

      const data: AuthResponse = await response.json();
      
      if (data.success && data.token && data.user) {
        // Backend returns a custom token - exchange it for an ID token
        try {
          const userCredential = await signInWithCustomToken(auth, data.token);
          const idToken = await userCredential.user.getIdToken();
          
          // Store the ID token instead of custom token
          this.storeAuth(idToken, data.user);
          
          return {
            ...data,
            token: idToken  // Return ID token
          };
        } catch (firebaseError: any) {
          throw new Error('Failed to complete registration. Please try again.');
        }
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Verify current token
   */
  async verifyToken(): Promise<boolean> {
    if (!this.token) {
      return false;
    }

    try {
      const token = await this.getValidToken();
      if (!token) return false;
      const response = await fetch(`${this.baseUrl}/api/auth/verify-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        this.clearAuth();
        return false;
      }

      const data = await response.json();
      
      if (data.success && data.user) {
        this.user = data.user;
        localStorage.setItem('auth_user', JSON.stringify(data.user));
        return true;
      } else {
        this.clearAuth();
        return false;
      }
    } catch (error) {
      this.clearAuth();
      return false;
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    const uid = this.user?.uid;
    // End API access immediately; a pending IndexedDB delete must not let a
    // token refresh restore this session or clear a subsequent sign-in.
    this.clearAuth();
    if (uid) {
      try {
        await deleteTrustedRecord(uid);
      } catch {
        // Local storage cleanup must not prevent sign-out.
      }
    }
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    return this.user;
  }

  /**
   * Get current token
   */
  getCurrentToken(): string | null {
    return this.token;
  }

  /** Obtain the current Firebase ID token, letting the SDK renew it when needed. */
  async getValidToken(): Promise<string | null> {
    const storedUser = this.user;
    if (!storedUser || !this.token) return null;

    await auth.authStateReady();
    if (this.user !== storedUser || !this.token) return null;
    const firebaseUser = auth.currentUser;
    if (!firebaseUser || firebaseUser.uid !== storedUser.uid) {
      this.clearAuth();
      return null;
    }

    // Do not force a network refresh on every request. Firebase renews an
    // expired/nearly expired ID token and otherwise returns the valid token.
    const token = await firebaseUser.getIdToken();
    // A pending refresh must never restore a signed-out or switched account.
    if (this.user !== storedUser || !this.token || auth.currentUser?.uid !== storedUser.uid) return null;
    this.token = token;
    localStorage.setItem('auth_token', token);
    return token;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.token !== null && this.user !== null;
  }

  /**
   * Get authorization header for API requests
   */
  getAuthHeader(): { Authorization: string } | {} {
    return this.token ? { Authorization: `Bearer ${this.token}` } : {};
  }

  /**
   * Store Firebase authentication data (for Google OAuth)
   */
  storeFirebaseAuth(token: string, user: User): void {
    this.storeAuth(token, user);
  }
}

// Export singleton instance
export const secureAuthService = new SecureAuthService();
export default secureAuthService;
